'use client'

import { type Locale, type TranslationValues, useI18n } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import { DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import { useRouter } from 'next/navigation'
import React from 'react'
import { match, P } from 'ts-pattern'

import { ButtonV1, Flex, Typography, VStack } from '@podo-app/design-system-temp'

import { formatCouponDate, formatCouponDiscountText, formatCouponMaxDiscountText } from '../../lib'
import { CouponModel } from '../../model'
import { CouponCardProps, CouponCardTitleProps } from '../../ui/coupon-card/coupon-card.types'
import { CountDownTimeBadge, DdayBadge } from '../../ui/coupon-remaining-time-badge'

type Translate = (key: string, values?: TranslationValues) => string
type CouponI18nProps = { locale: Locale; t: Translate }

const CouponCard = (props: CouponCardProps) => {
  const router = useRouter()
  const { effectiveLocale, t } = useI18n()
  const timezone = props.timezone ?? DEFAULT_TIMEZONE
  return (
    <Flex direction="vertical" className="p-5 w-full gap-5 rounded-lg border-[1px]">
      {match(props)
        .with({ status: P.union(CouponModel.STATUS.NORMAL, CouponModel.STATUS.PENDING) }, (availableProps) => (
          <AvailableCouponCard {...availableProps} locale={effectiveLocale} timezone={timezone} t={t} />
        ))
        .with({ status: CouponModel.STATUS.USED }, (usedProps) => (
          <UsedCouponCard {...usedProps} locale={effectiveLocale} timezone={timezone} t={t} />
        ))
        .with({ status: CouponModel.STATUS.EXPIRED }, (expiredProps) => (
          <ExpiredCouponCard {...expiredProps} locale={effectiveLocale} timezone={timezone} t={t} />
        ))
        .exhaustive()}

      <ButtonV1
        variant="sub"
        size="large"
        className="w-full"
        onClick={() => router.push(`/my-podo/coupon/${props.id}`)}
        eventParams={{ name: UI_IDS.btn.viewCouponDetail, location: 'coupon', action: 'navigate' }}
      >
        {t('coupon.actions.viewDetail')}
      </ButtonV1>
    </Flex>
  )
}

const CouponCardContent = ({
  title,
  children,
  headerContent,
}: {
  title: string
  children: React.ReactNode
  headerContent?: React.ReactNode
}) => (
  <Flex direction="vertical" className="gap-2">
    <Flex className="justify-between">{headerContent}</Flex>
    <Typography size="h3">{title}</Typography>
    <VStack>{children}</VStack>
  </Flex>
)

const AvailableCouponCard = (
  props: Omit<CouponCardProps & { status: typeof CouponModel.STATUS.NORMAL }, 'status'> &
    CouponI18nProps & { timezone: string },
) => (
  <CouponCardContent
    title={props.title}
    headerContent={
      <>
        <CouponCardTitle
          status={CouponModel.STATUS.NORMAL}
          discountType={props.discountType}
          discountAmount={props.discountAmount}
          isSubscribePayment={props.isSubscribePayment}
          locale={props.locale}
          t={props.t}
        />
        {match(props.remainingTimeType)
          .with('TIME', () => <CountDownTimeBadge endDate={props.useEndedAt} />)
          .with('DDAY', () => <DdayBadge endDate={props.useEndedAt} />)
          .with('NON_DISPLAY', () => null)
          .exhaustive()}
      </>
    }
  >
    <Typography size="body1" className="text-gray-400">
      {`${formatCouponDate(props.useStartedAt, props.timezone, props.locale)} ~ ${formatCouponDate(
        props.useEndedAt,
        props.timezone,
        props.locale,
      )}`}
    </Typography>
    {props.isPercentageDiscount && !props.isMaximumDiscount && (
      <Typography size="body1" className="text-gray-400 ">
        {formatCouponMaxDiscountText({
          discountAmountMax: props.discountAmountMax,
          isSubscribePayment: props.isSubscribePayment,
          locale: props.locale,
          t: props.t,
        })}
      </Typography>
    )}
  </CouponCardContent>
)

const UsedCouponCard = (
  props: Omit<CouponCardProps & { status: typeof CouponModel.STATUS.USED }, 'status'> &
    CouponI18nProps & { timezone: string },
) => (
  <CouponCardContent
    title={props.title}
    headerContent={
      <CouponCardTitle
        status={CouponModel.STATUS.USED}
        discountType={props.discountType}
        discountAmount={props.discountAmount}
        isSubscribePayment={props.isSubscribePayment}
        locale={props.locale}
        t={props.t}
      />
    }
  >
    <Typography size="body1" className="text-red-normal">
      {props.t('coupon.status.usedOn', {
        date: formatCouponDate(props.usedAt, props.timezone, props.locale),
      })}
      {props.isPercentageDiscount && !props.isMaximumDiscount && (
        <>
          <br />
          {formatCouponMaxDiscountText({
            discountAmountMax: props.discountAmountMax,
            isSubscribePayment: props.isSubscribePayment,
            locale: props.locale,
            t: props.t,
          })}
        </>
      )}
    </Typography>
  </CouponCardContent>
)

const ExpiredCouponCard = (
  props: Omit<CouponCardProps & { status: typeof CouponModel.STATUS.EXPIRED }, 'status'> &
    CouponI18nProps & { timezone: string },
) => (
  <CouponCardContent
    title={props.title}
    headerContent={
      <CouponCardTitle
        status={CouponModel.STATUS.EXPIRED}
        discountType={props.discountType}
        discountAmount={props.discountAmount}
        isSubscribePayment={props.isSubscribePayment}
        locale={props.locale}
        t={props.t}
      />
    }
  >
    <Typography size="body1" className="text-red-normal">
      {props.t('coupon.status.expiredOn', {
        date: formatCouponDate(props.useEndedAt, props.timezone, props.locale),
      })}
    </Typography>
    {props.isPercentageDiscount && !props.isMaximumDiscount && (
      <Typography size="body1" className="text-gray-400">
        {formatCouponMaxDiscountText({
          discountAmountMax: props.discountAmountMax,
          isSubscribePayment: false,
          locale: props.locale,
          t: props.t,
        })}
      </Typography>
    )}
  </CouponCardContent>
)

const CouponCardTitle = ({
  status,
  discountType,
  discountAmount,
  isSubscribePayment,
  locale,
  t,
}: CouponCardTitleProps & CouponI18nProps) => {
  const titleColor = match(status)
    .with(P.union(CouponModel.STATUS.NORMAL, CouponModel.STATUS.PENDING), () => 'text-green-500')
    .with(P.union(CouponModel.STATUS.USED, CouponModel.STATUS.EXPIRED), () => 'text-gray-400')
    .exhaustive()

  const discountText = formatCouponDiscountText({
    discountType,
    discountAmount,
    isSubscribePayment,
    includeDiscountLabel: true,
    locale,
    t,
  })
  return <Typography className={`${titleColor} font-bold text-[22px]`}>{discountText}</Typography>
}

export default CouponCard
